// React
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
// ライブラリ
import Swal from 'sweetalert2';
// カスタムフック
import { useEditStateStorage } from '../../../hooks/useEditStateStorage';
// ユーティリティ
import { validationSchema } from '../../../utils/validationUtils';
import { errorMessages, successMessages } from '../../../utils/messagesUtils';
import { menu } from '../../../constants';
// スタイル
import sty from './Main.module.css';

const Works = () => {
    const navigate = useNavigate()
    const [originalData, setOriginalData] = useState(null);

    const defaultWorks = {
        works: {
            work_pdf_name: '',
            work_pdf: '',
            work_list: [{ work_name: '', work_url: '' }]
        }
    };

    // フォームのstateを管理
    const { handleSubmit, control, formState: { errors }, reset, watch, getValues } = useForm({
        mode: 'onSubmit', // 保存時にバリデーションを実行
        reValidateMode: 'onSubmit', // 一度エラーが発生した後も、次の保存時にのみ再バリデーションを実行
        defaultValues: defaultWorks,
    });

    // useFieldArray の設定
    const { fields, append, remove } = useFieldArray({ name: 'works.work_list', control });

    // アップロードファイルの一時URLを管理
    const [portfolioUrl, setPortfolioUrl] = useState(null);

    const watchAllFields = watch();
    const getFormData = useCallback(() => watchAllFields, [watchAllFields]);

    const { checkForEditData, clearStoredData } = useEditStateStorage(
        'works',          // ストレージキープレフィックス
        getFormData,     // フォームデータ取得関数
        reset,            // フォームリセット関数
        originalData,     // オリジナルデータ
    );

    const fetchData = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/work`, {
                method: "GET",
                headers: {
                    'Accept': "application/json",
                    "Content-Type": "application/json",
                },
                credentials: 'include'
            });
            const result = await response.json();
            if(response.status === 200 && result.status === 'OK'){
                const data = result?.data;
                if(data) {
                    if(Object.keys(data).length === 0) {
                        setOriginalData(defaultWorks);
                        reset(defaultWorks);
                        checkForEditData(defaultWorks);
                    } else {
                        let fileObj = null;
                        // DBから取ってきたpdfの情報からfileオブジェクトを作成（再度postができる形へ変換）
                        if (data?.work_pdf) {
                            fileObj = await getPdfUrl(data?.work_pdf)
                        }
                        const formattedData = {
                            works: {
                                work_pdf_name: data?.work_pdf_name || '',
                                work_pdf: fileObj || '',
                                work_list: data?.work_list || [{ work_name: '', work_url: '' }],
                            }
                        };
                        setOriginalData(formattedData);
                        reset(formattedData);
                        checkForEditData(formattedData);
                    }
                }
            } else {
                if (response.status === 401) {
                    navigate(menu.ENGINEER.SIGNIN.PATH);
                } else if (response.status === 404) {
                    const error = new Error();
                    error.front_message = errorMessages.common.data_not_found;
                    throw error;
                } else {
                    const error = new Error();
                    error.front_message = errorMessages.common.data_fetch_failed;
                    throw error;
                }
            }
        } catch (e) {
            Swal.fire({
				icon: 'error',
				html: `${e.front_message || errorMessages.api.server_error}`,
                customClass: {
                    htmlContainer: 'large-text'
                }
			});
        }
    };

    // 初期化
	useEffect(() => {
		if (originalData?.work_pdf) {
			getPdfUrl(originalData.work_pdf)
		}
		// コンポーネントのアンマウント時にURLを無効化
		return () => {
			if (portfolioUrl) {
				URL.revokeObjectURL(portfolioUrl)
			}
		}
	}, [originalData?.work_pdf])

    // ポートフォリオ取得
	const getPdfUrl = async (pdfData) => {
		if (!pdfData) {
			return;
		}
		try {
			const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/work/portfolio/${pdfData?.filename}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
				}
			)
			if (response.status === 200) {
				const blob = await response.blob()
				// 古いURLを無効化
				if (portfolioUrl) {
					URL.revokeObjectURL(portfolioUrl)
				}
				const newUrl = URL.createObjectURL(blob)
				setPortfolioUrl(newUrl)
                const fileObj = new File([blob], pdfData.originalname, { type: pdfData.mimetype, lastModified: Date.now() });
                return fileObj
			}else{
				const error = new Error();
				error.front_message = errorMessages.works.portfolio_fetch_failed;
				throw error;
			}
		} catch (e) {
            Swal.fire({
				icon: 'error',
				html: `${e.front_message || errorMessages.api.server_error}`,
                customClass: {
                    htmlContainer: 'large-text'
                }
			});
		}
	}

    useEffect(() => { 
        fetchData(); 
    }, []);

    useEffect(() => { 
        if (fields.length === 0) 
            append({ work_name: '', work_url: '' }); 
    }, [fields, append]);

    // エンターキーでの送信を防ぐ
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && e.target.tagName !== 'BUTTON') {
            e.preventDefault();
        }
    };

    const isValidFile = (fileValue) => {
        return fileValue instanceof File && fileValue.name;
    };

    // フォーム送信時の処理
    const onSubmit = async (data) => {
        const formattedData = {
            works: {
                work_pdf_name: data.works.work_pdf_name,
                work_pdf: data.works.work_pdf,
                work_list: data.works.work_list.filter(item => Object.values(item).some(value => value !== ''))
            }
        };
        
        // ファイルを含むデータの送信に使用
        const formData = new FormData();
        
        // ポートフォリオを登録する場合
        if (formattedData.works.work_pdf_name && formattedData.works.work_pdf instanceof File) {
            formData.append('work_pdf_name', formattedData.works.work_pdf_name);
            formData.append('work_pdf', formattedData.works.work_pdf);
        }

        // 実績リストを登録する場合
        formattedData.works.work_list.forEach((item, index) => {
            if (item.work_name) {
                formData.append(`work_list[${index}][work_name]`, item.work_name);
            }
            if (item.work_url) {
                formData.append(`work_list[${index}][work_url]`, item.work_url);
            }
        });

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/work`, {
                method: "POST",
                body: formData,
                credentials: "include",
            });
            const result = await response.json();
            if (response.status === 200 && result.status === 'OK') {
                clearStoredData();
                reset(formattedData);
                setOriginalData(formattedData);
                Swal.fire({ icon: 'success', title: successMessages.operation.created });
            } else {
                if (response.status === 401) {
                    navigate(menu.ENGINEER.SIGNIN.PATH)
                } else if (response.status === 413) {
                    // ファイルサイズ超過エラー
                    const error = new Error();
                    error.icon = 'warning';
                    error.front_message = errorMessages.works.upload_file_size_exceeded;
                    throw error;
                } else if (response.status === 415) {
                    // ファイル形式エラー
                    const error = new Error();
                    error.icon = 'warning';
                    error.front_message = errorMessages.works.upload_file_format_invalid;
                    throw error;
                } else if (response.status === 400) {
                    // その他のアップロードエラー
                    const error = new Error();
                    error.front_message = errorMessages.works.upload_failed;
                    throw error;
                } else {
                    throw new Error();
                }
            }
        } catch (e) {
            await Swal.fire({
                icon: e.icon || 'error',
                html: `${e.front_message || errorMessages.api.server_error}`,
                customClass: {
                    htmlContainer: 'large-text'
                }
            });
        }
    };

    useEffect(() => {
        // ローカルストレージから復元したデータのファイルチェック
        const currentValue = getValues('works.work_pdf');
        if (!isValidFile(currentValue) && portfolioUrl) {
            // ファイルが有効でない場合はURLもクリア
            setPortfolioUrl(null);
        }
    }, []);

    return (
        <form onSubmit={handleSubmit(onSubmit)} onKeyDown={handleKeyDown} className={sty['form']}>
            <div className={`${sty['form-tab__content-inner']} ${sty['form-tab__content-inner-pt']}`}>
                <table className={sty['form__qualification-pc']}>
                    <tbody>
                        <tr>
                            <th className={sty['work_tit']}>ポートフォリオ名</th>
                            <th className={sty['work_url']}>PDFファイル</th>
                            {fields.length > 1 && (
                                <th className={sty['table__blank-th']}></th>
                                )}
                        </tr>
                        <tr>
                            <td className={sty['work_tit']}>
                                <Controller
                                    name="works.work_pdf_name"
                                    control={control}
                                    rules={{ ...validationSchema.work_pdf_name(watch('works.work_pdf'))}}
                                    render={({ field }) => <input {...field} placeholder="ポートフォリオ名" />}
                                />
                                {errors.works?.work_pdf_name && <p className="form__validation-msg">{errors.works.work_pdf_name.message}</p>}
                            </td>
                            <td className={sty['work_url']} colSpan="2">
                                <Controller
                                    name="works.work_pdf"
                                    control={control}
                                    rules={validationSchema.work_pdf_file(watch('works.work_pdf_name'))}
                                    render={({ field }) => (
                                        <>
                                            <input type="file" accept="application/pdf" id="hiddenFileInputPC" style={{ display: 'none' }} onChange={(e) => {
                                                const file = e.target.files[0];
                                                field.onChange(file);
                                                setPortfolioUrl(file ? URL.createObjectURL(file) : '');
                                            }} />
                                            <div className={sty['work_pdf']}>
                                                <button
                                                    type="button"
                                                    className={sty['file-select-button']}
                                                    onClick={() => document.getElementById('hiddenFileInputPC').click()}>
                                                    ファイルを選択
                                                </button>
                                                {portfolioUrl && isValidFile(field.value) && (
                                                    <span>
                                                        <a
                                                            href={portfolioUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={sty['file-name-display']}
                                                            type="application/pdf"
                                                        >
                                                            {field.value?.name || watch('works.work_pdf.filename')}
                                                        </a>
                                                        <button
                                                            type="button"
                                                            className={sty['file-delete-icon-btn']}
                                                            aria-label="ファイル削除"
                                                            onClick={() => {
                                                                field.onChange('');
                                                                if (portfolioUrl) {
                                                                    URL.revokeObjectURL(portfolioUrl);
                                                                    setPortfolioUrl('');
                                                                }
                                                                document.getElementById('hiddenFileInputPC').value = '';
                                                            }}>
                                                            <img src="/images/cross.png" alt="削除" width="15" height="15" />
                                                        </button>
                                                    </span>
                                                )}
                                            </div>
                                            {errors.works?.work_pdf && <p className="form__validation-msg">{errors.works.work_pdf.message}</p>}
                                        </>
                                    )}
                                    />
                            </td>
                        </tr>
                        <tr>
                            <th className={sty['work_tit']}>実績名</th>
                            <th className={sty['work_url']}>URL</th>
                            {fields.length > 1 && <th className={sty['table__blank-th']}></th>}
                        </tr>
                        {fields.map((field, index) => (
                            <tr key={field.id}>
                                <td className={sty['work_tit']}>
                                    <Controller
                                        name={`works.work_list.${index}.work_name`}
                                        control={control}
                                        rules={{ ...validationSchema.work_name(watch(`works.work_list.${index}.work_url`)) }}
                                        render={({ field }) => <input {...field} placeholder="ポートフォリオ, GitHub, Qiita" />}
                                    />
                                    {errors.works?.work_list?.[index]?.work_name && <p className="form__validation-msg">{errors.works.work_list[index].work_name.message}</p>}
                                </td>
                                <td className={sty['work_url']}>
                                    <Controller
                                        name={`works.work_list.${index}.work_url`}
                                        control={control}
                                        rules={{ ...validationSchema.work_url(watch(`works.work_list.${index}.work_name`)) }}
                                        render={({ field }) => <input {...field} placeholder="https://" />}
                                    />
                                    {errors.works?.work_list?.[index]?.work_url && (
                                        <p className="form__validation-msg">
                                            {errors.works.work_list[index].work_url.message}
                                        </p>
                                    )}
                                </td>
                                {fields.length > 1 && (
                                    <td className={sty['work_delete']}>
                                        <button
                                            type="button"
                                            className={sty['delete-each-btn']}
                                            onClick={() => remove(index)}
                                        >
                                            削除
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className={sty['form__btn-left']}>
                    <button type="button" className={`${sty['plus']} ${sty['plus-pc']}`} onClick={() => append({ work_name: '', work_url: '' })}>
                        +
                    </button>
                </div>
                {/* SP版 */}
                <div className={sty['form__qualification-sp']}>
                    <div className={sty['form__flex']}>
                        <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                            <div className={sty['form__label']}>ポートフォリオ名</div>
                        </div>
                        <div>
                            <div className={`${sty['form__input']} ${sty['form__flex-sp-mb']}`}>
                                <Controller
                                    name="works.work_pdf_name"
                                    control={control}
                                    rules={validationSchema.work_pdf_name(watch('works.work_pdf'))}
                                    render={({ field }) => (
                                        <input {...field} type="text" placeholder="ポートフォリオ名" />
                                    )}
                                />
                                {errors.works?.work_pdf_name && (
                                    <p className="form__validation-msg">{errors.works.work_pdf_name.message}</p>
                                )}
                            </div>
                        </div>
                        <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                            <div className={sty['form__label']}>PDFファイル</div>
                        </div>
                        <div className={sty['work_url']}>
                                <Controller
                                    name="works.work_pdf"
                                    control={control}
                                    rules={validationSchema.work_pdf_file(watch('works.work_pdf_name'))}
                                    render={({ field }) => (
                                        <>
                                            <input type="file" accept="application/pdf" id="hiddenFileInputSP" style={{ display: 'none' }} onChange={(e) => {
                                                const file = e.target.files[0];
                                                field.onChange(file);
                                                setPortfolioUrl(file ? URL.createObjectURL(file) : '');
                                            }} />
                                            <div className={sty['sp_work_pdf']}>
                                                <button
                                                    type="button" 
                                                    className={sty['file-select-button']}
                                                    onClick={() => document.getElementById('hiddenFileInputSP').click()}>
                                                    ファイルを選択
                                                </button>
                                                <br />
                                                {portfolioUrl && isValidFile(field.value) && (
                                                    <span>
                                                        <a href={portfolioUrl} target="_blank" rel="noopener noreferrer" className={sty['file-name-display']}>
                                                            {field.value?.name || watch('works.work_pdf.filename')}
                                                        </a>
                                                        <button
                                                            type="button"
                                                            className={sty['file-delete-icon-btn']}
                                                            aria-label="ファイル削除"
                                                            onClick={() => {
                                                                field.onChange('');
                                                                if (portfolioUrl) {
                                                                    URL.revokeObjectURL(portfolioUrl);
                                                                    setPortfolioUrl('');
                                                                }
                                                                document.getElementById('hiddenFileInputSP').value = '';
                                                            }}
                                                        >
                                                            <img src="/images/cross.png" alt="削除" width="15" height="15" />
                                                        </button>
                                                    </span>
                                                )}
                                            </div>
                                            {errors.works?.work_pdf && <p className="form__validation-msg">{errors.works.work_pdf.message}</p>}
                                        </>
                                    )}
                                />
                        </div>
                    </div>
                </div>
            </div>

            {fields.map((field, index) => {
                const work_name = `works.work_list.${index}.work_name`
                const work_url = `works.work_list.${index}.work_url`

                return(    
                    <div className={`${sty['form-tab__content-inner']} ${sty['form-tab__content-inner-sp']}`} key={field.id}>
                        <div className={sty['form__qualification-sp']}>
                            <div className={sty['form__flex']}>
                                <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                                    <div className={sty['form__label']}>実績名</div>
                                </div>
                                <div className={sty['form__flex']}>
                                    <div className={`${sty['form__input']} ${sty['form__flex-sp-mb']}`}>
                                        <Controller
                                            name={work_name}
                                            control={control}
                                            rules={{ ...validationSchema.work_name(watch(work_url)) }}
                                            render={({ field }) => (
                                                <input {...field} type="text" placeholder="ポートフォリオ, GitHub, Qiita" />
                                            )}
                                        />
                                        {errors.works?.work_list?.[index]?.work_name && (
                                            <p className="form__validation-msg">
                                                {errors.works.work_list[index].work_name.message}
                                            </p>
                                        )}
                                    </div>
                                    <div className={`${sty['form__input']} ${sty['form__flex-sp-mb']}`}>
                                        <div className={`${sty['form__label']} ${sty['form__label-flex-mb']} ${sty['form__label_txt']}`}>URL</div>
                                    </div>
                                    <div className={sty['form__input']}>
                                        <Controller
                                            name={work_url}
                                            control={control}
                                            rules={{ ...validationSchema.work_url(watch(work_name)) }}
                                            render={({ field }) => (
                                                <input {...field} type="text" placeholder="https://" />
                                            )}
                                        />
                                        {errors.works?.work_list?.[index]?.work_url && (
                                            <p className="form__validation-msg">
                                                {errors.works.work_list[index].work_url.message}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                {fields.length > 1 && (
                                    <div className={sty['work_delete']}>
                                        <button
                                            type="button"
                                            className={sty['delete-each-btn']}
                                            onClick={() => remove(index)}
                                        >
                                            削除
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            )}

            <div className={sty['form__btn-left']}>
                <button
                    type="button"
                    className={`${sty['plus']} ${sty['plus-sp']}`}
                    onClick={() =>
                        append({ work_name: '', work_url: '' })
                    }
                >
                    +
                </button>
            </div>
            <div className="primary-btns">
                <button type="submit" className="primary-btn">保存</button>
            </div>
        </form>
    );

};

export default Works;
